"use client";

import { useMemo } from "react";
import { useBinanceOrderBook } from "@/hooks/useBinanceOrderBook";

interface LiquidityHeatmapProps {
  symbol: string;
  tokenName: string;
}

interface Bucket {
  priceLow: number;
  priceHigh: number;
  bidNotional: number;
  askNotional: number;
}

function formatUsd(value: number): string {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

function formatPrice(price: number): string {
  if (price < 0.01) return price.toFixed(6);
  if (price < 1) return price.toFixed(4);
  if (price < 100) return price.toFixed(3);
  return price.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function LiquidityHeatmap({ symbol, tokenName }: LiquidityHeatmapProps) {
  const { snapshot, loading, error } = useBinanceOrderBook(symbol, 1000, 15000);

  const { buckets, maxBucketNotional, midPrice, priceRange } = useMemo(() => {
    if (!snapshot) {
      return {
        buckets: [] as Bucket[],
        maxBucketNotional: 0,
        midPrice: 0,
        priceRange: { min: 0, max: 0 },
      };
    }

    const allLevels = [...snapshot.bids, ...snapshot.asks];
    if (allLevels.length === 0) {
      return {
        buckets: [] as Bucket[],
        maxBucketNotional: 0,
        midPrice: snapshot.midPrice,
        priceRange: { min: 0, max: 0 },
      };
    }

    // Restrict to ±5% band around mid for a focused heatmap
    const mid = snapshot.midPrice;
    const minPrice = mid * 0.95;
    const maxPrice = mid * 1.05;

    const BUCKET_COUNT = 40;
    const bucketSize = (maxPrice - minPrice) / BUCKET_COUNT;
    const bucketArr: Bucket[] = [];
    for (let i = 0; i < BUCKET_COUNT; i++) {
      bucketArr.push({
        priceLow: minPrice + i * bucketSize,
        priceHigh: minPrice + (i + 1) * bucketSize,
        bidNotional: 0,
        askNotional: 0,
      });
    }

    for (const bid of snapshot.bids) {
      if (bid.price < minPrice || bid.price > maxPrice) continue;
      const idx = Math.min(
        BUCKET_COUNT - 1,
        Math.floor((bid.price - minPrice) / bucketSize),
      );
      bucketArr[idx]!.bidNotional += bid.notional;
    }
    for (const ask of snapshot.asks) {
      if (ask.price < minPrice || ask.price > maxPrice) continue;
      const idx = Math.min(
        BUCKET_COUNT - 1,
        Math.floor((ask.price - minPrice) / bucketSize),
      );
      bucketArr[idx]!.askNotional += ask.notional;
    }

    const maxNotional = Math.max(
      ...bucketArr.map((b) => Math.max(b.bidNotional, b.askNotional)),
    );

    return {
      buckets: bucketArr.reverse(), // top = highest price
      maxBucketNotional: maxNotional,
      midPrice: mid,
      priceRange: { min: minPrice, max: maxPrice },
    };
  }, [snapshot]);

  if (error === "not_available") {
    return (
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] p-6">
        <h3 className="text-lg font-semibold text-white mb-2">Liquidity Heatmap</h3>
        <p className="text-sm text-gray-500">
          Order book data unavailable for this token. Liquidity heatmaps require
          a Binance spot pair.
        </p>
      </div>
    );
  }

  if (loading || !snapshot) {
    return (
      <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] p-6 animate-pulse">
        <div className="h-5 bg-[hsl(230,15%,14%)] rounded w-1/3 mb-3" />
        <div className="h-96 bg-[hsl(230,15%,10%)] rounded" />
      </div>
    );
  }

  const spread = snapshot.asks[0] && snapshot.bids[0]
    ? ((snapshot.asks[0].price - snapshot.bids[0].price) / snapshot.midPrice) * 100
    : 0;

  return (
    <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(230,15%,8%)] overflow-hidden">
      <div className="p-4 border-b border-[hsl(var(--border))]">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-lg font-semibold text-white">Liquidity Heatmap</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {tokenName} order book depth (±5% from mid) — live every 15s
            </p>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-emerald-500" />
              <span className="text-gray-400">Bid liquidity</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-3 rounded-sm bg-red-500" />
              <span className="text-gray-400">Ask liquidity</span>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          <div className="bg-[hsl(230,15%,10%)] rounded-lg px-3 py-2">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Mid Price</p>
            <p className="text-sm font-mono font-semibold text-white">
              ${formatPrice(midPrice)}
            </p>
          </div>
          <div className="bg-[hsl(230,15%,10%)] rounded-lg px-3 py-2">
            <p className="text-[10px] text-gray-500 uppercase tracking-wide">Spread</p>
            <p className="text-sm font-mono font-semibold text-white">
              {spread.toFixed(3)}%
            </p>
          </div>
          <div className="bg-[hsl(230,15%,10%)] rounded-lg px-3 py-2">
            <p className="text-[10px] text-emerald-500 uppercase tracking-wide">Bid Depth</p>
            <p className="text-sm font-mono font-semibold text-emerald-400">
              {formatUsd(snapshot.totalBidLiquidity)}
            </p>
          </div>
          <div className="bg-[hsl(230,15%,10%)] rounded-lg px-3 py-2">
            <p className="text-[10px] text-red-500 uppercase tracking-wide">Ask Depth</p>
            <p className="text-sm font-mono font-semibold text-red-400">
              {formatUsd(snapshot.totalAskLiquidity)}
            </p>
          </div>
        </div>
      </div>

      {/* Heatmap rows */}
      <div className="p-4">
        <div className="space-y-0.5">
          {buckets.map((bucket, i) => {
            const bidIntensity = maxBucketNotional > 0
              ? bucket.bidNotional / maxBucketNotional
              : 0;
            const askIntensity = maxBucketNotional > 0
              ? bucket.askNotional / maxBucketNotional
              : 0;
            const isMidBucket = midPrice >= bucket.priceLow && midPrice <= bucket.priceHigh;
            const avgPrice = (bucket.priceLow + bucket.priceHigh) / 2;

            return (
              <div
                key={i}
                className={`relative flex items-center h-5 text-[10px] font-mono ${
                  isMidBucket ? "border-y border-yellow-500/50" : ""
                }`}
              >
                {/* Price label (left) */}
                <div className="w-20 pr-2 text-right text-gray-500 shrink-0">
                  ${formatPrice(avgPrice)}
                </div>

                {/* Bar container */}
                <div className="flex-1 relative h-full flex">
                  {/* Bid side (left half, right-aligned) */}
                  <div className="flex-1 h-full flex justify-end pr-px">
                    {bidIntensity > 0 && (
                      <div
                        className="h-full rounded-sm transition-all"
                        style={{
                          width: `${bidIntensity * 100}%`,
                          backgroundColor: `rgba(34, 197, 94, ${Math.max(0.15, bidIntensity)})`,
                          boxShadow: bidIntensity > 0.7 ? "0 0 8px rgba(34, 197, 94, 0.5)" : undefined,
                        }}
                      />
                    )}
                  </div>
                  {/* Center divider */}
                  <div className={`w-px h-full ${isMidBucket ? "bg-yellow-500" : "bg-[hsl(230,15%,18%)]"}`} />
                  {/* Ask side (right half, left-aligned) */}
                  <div className="flex-1 h-full flex justify-start pl-px">
                    {askIntensity > 0 && (
                      <div
                        className="h-full rounded-sm transition-all"
                        style={{
                          width: `${askIntensity * 100}%`,
                          backgroundColor: `rgba(239, 68, 68, ${Math.max(0.15, askIntensity)})`,
                          boxShadow: askIntensity > 0.7 ? "0 0 8px rgba(239, 68, 68, 0.5)" : undefined,
                        }}
                      />
                    )}
                  </div>
                </div>

                {/* Notional label (right) */}
                <div className="w-24 pl-2 text-left text-gray-500 shrink-0">
                  {bucket.bidNotional > 0 || bucket.askNotional > 0
                    ? formatUsd(Math.max(bucket.bidNotional, bucket.askNotional))
                    : ""}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-4 py-2 border-t border-[hsl(var(--border))] text-[10px] text-gray-500">
        Yellow band = current mid price. Brighter bars indicate stronger walls where
        large orders are clustered. Watch for asymmetries to spot accumulation or
        distribution zones.
      </div>
    </div>
  );
}
